/**
 * Main App Component
 * Root component with authentication and routing
 */

import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './index.css';

// Context Providers
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';
import { NotificationProvider } from './contexts/NotificationContext';

// Components
import Sidebar from './components/Sidebar';
import NotificationBell from './components/NotificationBell';
import { Breadcrumb } from './components/ui';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import UpdateBanner from './components/desktop/UpdateBanner';
import { useDisableNumberInputScroll } from './hooks/useDisableNumberInputScroll';
import { ACTIVATION_ONLY_ROLES, ACTIVATION_ROLES, NOVELTY_PIPELINE_ROLES, SALES_DATA_ROLES } from './constants/activationRoles';
import { noveltyPipelineEnabled, shipmentTimelineEnabled } from './constants/featureFlags';

// Pages — lazy-loaded so each route ships its own chunk instead of one
// eager bundle. Layout chrome above (Sidebar/Breadcrumb/guards) stays eager
// because it renders on every authenticated route.
const Login = lazy(() => import('./pages/Login'));
const Home = lazy(() => import('./pages/Home'));
const PriceCalculator = lazy(() => import('./pages/PriceCalculator'));
const Brands = lazy(() => import('./pages/Brands'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const ExistingBrandBusinessPlans = lazy(() => import('./pages/existingBrandBusinessPlan/ExistingBrandBusinessPlans'));
const ExistingBrandBusinessPlanPage = lazy(() => import('./pages/existingBrandBusinessPlan/ExistingBrandBusinessPlanPage'));
const SupplierReportTemplates = lazy(() => import('./pages/SupplierReportTemplates'));
const BrandGroups = lazy(() => import('./pages/BrandGroups'));
const Currencies = lazy(() => import('./pages/Currencies'));
const Markets = lazy(() => import('./pages/Markets'));
const Segments = lazy(() => import('./pages/Segments'));
const DoorSales = lazy(() => import('./pages/doorSales/DoorSalesPage'));
const SalesReporting = lazy(() => import('./pages/SalesReporting'));
const OutpostSales = lazy(() => import('./pages/OutpostSales'));
const NewProductLaunchList = lazy(() => import('./pages/NewProductLaunchList'));
const ArrivalPipeline = lazy(() => import('./pages/ArrivalPipeline'));
const ShopifyStock = lazy(() => import('./pages/ShopifyStock'));
const InventreeParts = lazy(() => import('./pages/InventreeParts'));
const NoveltyPipeline = lazy(() => import('./pages/NoveltyPipeline'));
const NoveltyPipelineRedirect = lazy(() => import('./pages/NoveltyPipeline').then(m => ({ default: m.NoveltyPipelineRedirect })));
const DataConsistency = lazy(() => import('./pages/DataConsistency'));
const PriceStructures = lazy(() => import('./pages/PriceStructures'));
const Budgets = lazy(() => import('./pages/Budgets'));
const Templates = lazy(() => import('./pages/Templates'));
const BrandListMrp = lazy(() => import('./pages/BrandListMrp'));
const UnconfirmedMrps = lazy(() => import('./pages/UnconfirmedMrps'));
const ProductList = lazy(() => import('./pages/ProductList'));
const Users = lazy(() => import('./pages/Users'));
const BusinessPlanPage = lazy(() => import('./pages/businessPlan/BusinessPlanPage'));
const BusinessPlans = lazy(() => import('./pages/businessPlan/BusinessPlans'));
const AIAssistant = lazy(() => import('./pages/businessPlan/AIAssistant'));
const ProductLineExtrapolator = lazy(() => import('./pages/businessPlan/ProductLineExtrapolator'));
const ProductLineExtrapolations = lazy(() => import('./pages/businessPlan/ProductLineExtrapolations'));
const EmailTemplatesPage = lazy(() => import('./pages/EmailTemplatesPage'));
const BrandRanking = lazy(() => import('./pages/BrandRanking'));
const Doors = lazy(() => import('./pages/Doors'));
const DoorChannels = lazy(() => import('./pages/DoorChannels'));
const PricingChannels = lazy(() => import('./pages/PricingChannels'));
const PurchaseOrdersData = lazy(() => import('./pages/PurchaseOrdersData'));
const BrandDoorWeights = lazy(() => import('./pages/BrandDoorWeights'));
const BeautyAdvisorSales = lazy(() => import('./pages/BeautyAdvisorSales'));
const AdvisorSalesExport = lazy(() => import('./pages/AdvisorSalesExport'));
const BaSetup = lazy(() => import('./pages/BaSetup'));
const SeasonalityCalendar = lazy(() => import('./pages/SeasonalityCalendar'));
const ComponentShowcase = lazy(() => import('./pages/ComponentShowcase'));
const BudgetProjections = lazy(() => import('./pages/budgetProjections/BudgetProjections'));
const BudgetProjectionPage = lazy(() => import('./pages/budgetProjections/BudgetProjectionPage'));
const ProjectionsV2 = lazy(() => import('./pages/budgetProjections/ProjectionsV2'));
const ProjectionV2Page = lazy(() => import('./pages/budgetProjections/ProjectionV2Page'));
const Inventory = lazy(() => import('./pages/inventory/Inventory'));
const PurchaseOrders = lazy(() => import('./pages/purchaseOrders/PurchaseOrders'));
const PurchaseOrderPage = lazy(() => import('./pages/purchaseOrders/PurchaseOrderPage'));
const InventoryTimeline = lazy(() => import('./pages/InventoryTimeline'));
const ShipmentTimeline = lazy(() => import('./pages/ShipmentTimeline'));

const BrandTargetsPage = lazy(() => import('./pages/brandTargets/BrandTargetsPage'));
const ProductSalesPage = lazy(() => import('./pages/productSales/ProductSalesPage'));
const BrandProductSalesPage = lazy(() => import('./pages/productSales/BrandProductSalesPage'));
const Notifications = lazy(() => import('./pages/Notifications'));
const GaDataPage = lazy(() => import('./pages/gaData/GaDataPage'));
const GaNewPoByDates = lazy(() => import('./pages/gaNewPo/GaNewPoByDates'));
const ApiKeysPage = lazy(() => import('./pages/admin/ApiKeysPage'));
const GaDetailPage = lazy(() => import('./pages/gaData/GaDetailPage'));
const BrandYoyAnalysisPage = lazy(() => import('./pages/productSales/BrandYoyAnalysisPage'));
const SOBMatrix = lazy(() => import('./pages/sob/SOBMatrix'));

// Marketing Pages
const MarketingCategories = lazy(() => import('./pages/marketing/masterData/MarketingCategories'));
const MarketingItems = lazy(() => import('./pages/marketing/masterData/MarketingItems'));
const Contractors = lazy(() => import('./pages/marketing/masterData/Contractors'));
const MarketingExpenses = lazy(() => import('./pages/marketing/MarketingExpenses'));
const CapexLineItems = lazy(() => import('./pages/capex/CapexLineItems'));
const PurchaseBPDashboard = lazy(() => import('./pages/purchaseBpDashboard/PurchaseBPDashboard'));
const MarketingQuotes = lazy(() => import('./pages/marketing/MarketingQuotes'));
const QuoteDetail = lazy(() => import('./pages/marketing/QuoteDetail'));
const MarketingReports = lazy(() => import('./pages/marketing/MarketingReports'));
const MarketingAnalytics = lazy(() => import('./pages/marketing/MarketingAnalytics'));
const ActivationsHub = lazy(() => import('./pages/marketing/activations/ActivationsHub'));
const ActivationDetail = lazy(() => import('./pages/marketing/activations/ActivationDetail'));
const MarketingProcesses = lazy(() => import('./pages/marketing/masterData/MarketingProcesses'));
const Invoices = lazy(() => import('./pages/marketing/Invoices'));
const InvoiceDetail = lazy(() => import('./pages/marketing/InvoiceDetail'));

/**
 * Page Loader
 * Suspense fallback shown briefly while a lazy-loaded route chunk downloads.
 * Matches the auth-loading spinner so the transition is visually consistent.
 */
const PageLoader = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="sr-only">Loading...</span>
    </div>
  </div>
);

/**
 * Default Redirect Component
 * Redirects users to appropriate home page based on their role
 */
const DefaultRedirect = () => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Activation team roles reach Marketing > Activation and nothing else, so
  // the Home page would 403 for them.
  if (ACTIVATION_ONLY_ROLES.includes(user?.role)) {
    return <Navigate to="/marketing/activations" replace />;
  }

  // Brand managers should be redirected to Marketing Budget page
  if (user?.role === 'brand_manager') {
    return <Navigate to="/budgets" replace />;
  }

  // All other users go to dashboard
  return <Navigate to="/" replace />;
};

/**
 * Home Redirect Component
 * Redirects brand managers to /budgets, shows Home for others
 */
const HomeRedirect = () => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Activation team roles reach Marketing > Activation and nothing else, so
  // the Home page would 403 for them.
  if (ACTIVATION_ONLY_ROLES.includes(user?.role)) {
    return <Navigate to="/marketing/activations" replace />;
  }

  // Brand managers should be redirected to Marketing Budget page
  if (user?.role === 'brand_manager') {
    return <Navigate to="/budgets" replace />;
  }

  // All other users see the Home page
  return (
    <MainLayout>
      <Home />
    </MainLayout>
  );
};

/**
 * Main Layout Component
 * Renders sidebar and main content for authenticated users
 */
const MainLayout = ({ children }) => {
  const { isAuthenticated, hasRole } = useAuth();
  const { isCollapsed, isMobile } = useSidebar();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="app-container min-h-screen bg-lm-bg-app dark:bg-dm-bg-app">
      <Sidebar />
      <main className={`main-content min-h-screen transition-all duration-300 ${isMobile ? 'ml-0' : isCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className={`px-2 ${isMobile ? 'pt-[3.5rem] pb-[4.25rem]' : 'py-3'}`}>
          {/* The bell is beside the breadcrumb rather than inside it: Breadcrumb
              renders null on the dashboard and on any route it has no entry for,
              and the dashboard is where someone lands when they sign in. */}
          <div className="d-flex align-items-start justify-content-between gap-2">
            <div className="flex-grow-1" style={{ minWidth: 0 }}><Breadcrumb hasRole={hasRole} /></div>
            <NotificationBell />
          </div>
          {children}
        </div>
      </main>
    </div>
  );
};

/**
 * App Routes Component
 * Defines all application routes
 */
const AppRoutes = () => {
  const { loading } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/components" element={<ComponentShowcase />} />
      
      {/* Protected Routes - Dashboard (not for brand managers) */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Price Calculator - not for brand managers */}
      <Route path="/calculator" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <PriceCalculator />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Master Data - not for brand managers */}
      <Route path="/brands" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <Brands />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/suppliers" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <Suppliers />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Supplier Report Templates: the roles /api/supplier-report-templates admits. */}
      <Route path="/supplier-report-templates" element={
        <ProtectedRoute requiredRoles={SALES_DATA_ROLES}>
          <MainLayout>
            <SupplierReportTemplates />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/brand-groups" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <BrandGroups />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Existing Brand Business Plan (EBBP) — sibling of the new-brand BP,
          seeded from historical actuals. The roles /api/existing-brand-business-plans admits. */}
      <Route path="/existing-brand-business-plans" element={
        <ProtectedRoute requiredRoles={SALES_DATA_ROLES}>
          <MainLayout>
            <ExistingBrandBusinessPlans />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/existing-brand-business-plan" element={
        <ProtectedRoute requiredRoles={SALES_DATA_ROLES}>
          <MainLayout>
            <ExistingBrandBusinessPlanPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/existing-brand-business-plan/:id" element={
        <ProtectedRoute requiredRoles={SALES_DATA_ROLES}>
          <MainLayout>
            <ExistingBrandBusinessPlanPage />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/currencies" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <Currencies />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/markets" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <Markets />
          </MainLayout>
        </ProtectedRoute>
      } />

      
      <Route path="/segments" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <Segments />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Admin-only, viewing included — see routes/doorSalesRoutes.js. */}
      <Route path="/door-sales" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <DoorSales />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Outpost Sales: the roles /api/outpost-sales admits; brand_manager is clamped to its suppliers */}
      <Route path="/outpost-sales" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'brand_manager']}>
          <MainLayout>
            <OutpostSales />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* New Product Launch List — shown under both Master Data and Marketing */}
      <Route path="/new-product-launch-list" element={
        <ProtectedRoute>
          <MainLayout>
            <NewProductLaunchList />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing/new-product-launch-list" element={
        <ProtectedRoute>
          <MainLayout>
            <NewProductLaunchList />
          </MainLayout>
        </ProtectedRoute>
      } />
      {/* Novelty Pipeline redirect: off with the page (REACT_APP_NOVELTY_PIPELINE_ENABLED). */}
      {noveltyPipelineEnabled() && (
        <Route path="/marketing/arrival-pipeline" element={<NoveltyPipelineRedirect />} />
      )}

      <Route path="/data-consistency" element={
        <ProtectedRoute requiredRoles={SALES_DATA_ROLES}>
          <MainLayout>
            <DataConsistency />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      {/* Price Management - not for brand managers */}
      <Route path="/price-structures" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <PriceStructures />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      {/* Brand Budgets: the roles /api/budgets admits; brand_manager is clamped to its suppliers */}
      <Route path="/budgets" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'brand_manager']}>
          <MainLayout>
            <Budgets />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/templates" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <Templates />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Brand List MRP (renamed from "Price Lists"). The legacy /price-lists
          path is kept as an alias so deep links and bookmarks keep working. */}
      <Route path="/brand-list-mrp" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <BrandListMrp />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/price-lists" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <BrandListMrp />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Work queue of List MRPs with no confirmed effective date. Reached from
          the Brand List MRP ingest notice and from the sidebar. */}
      <Route path="/brand-list-mrp/unconfirmed" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <UnconfirmedMrps />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Products - not for brand managers */}
      <Route path="/products" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <ProductList />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Sales Reporting: admin, manager, reporting_manager; /api/sales-report refuses every other role */}
      <Route path="/sales-reporting" element={
        <ProtectedRoute requiredRoles={SALES_DATA_ROLES}>
          <MainLayout>
            <SalesReporting />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Price Analysis is covered by Brand List MRP (unified Export + the
          round-trip upload). The redirect keeps bookmarks working. */}
      <Route path="/price-analysis" element={<Navigate to="/brand-list-mrp" replace />} />

      {/* Share of Business (SOB) Matrix */}
      <Route path="/sob-matrix" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <SOBMatrix />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      {/* User Management - Admin/Manager only */}
      <Route path="/users" element={
        <ProtectedRoute requiredRoles={['admin', 'manager']}>
          <MainLayout>
            <Users />
          </MainLayout>
        </ProtectedRoute>
      } />


      {/* Business Plans - not for brand managers */}
      <Route path="/business-plans" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <BusinessPlans />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Email Templates — managing the shared library is an admin/manager
          action, mirroring the writes gated by authorize('admin','manager')
          on /api/email-templates. */}
      <Route path="/email-templates" element={
        <ProtectedRoute requiredRoles={['admin', 'manager']}>
          <MainLayout>
            <EmailTemplatesPage />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/business-plan" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <BusinessPlanPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/business-plan/:id" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <BusinessPlanPage />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/business-plan/ai-assistant" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <AIAssistant />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/business-plan/product-line-extrapolations" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <ProductLineExtrapolations />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/business-plan/product-line-extrapolator" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <ProductLineExtrapolator />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/business-plan/product-line-extrapolator/:id" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <ProductLineExtrapolator />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/pricing-channels" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <PricingChannels />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Master Data - Purchase Orders (InvenTree PO database) - not for brand managers */}
      <Route path="/master-data/purchase-orders" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <PurchaseOrdersData />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Master Data - Doors - not for brand managers */}
      <Route path="/door-channels" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <DoorChannels />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/doors" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <Doors />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/brand-door-weights" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <BrandDoorWeights />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Beauty Advisor Sales: the roles the /api/ba-attribution reads admit. */}
      <Route path="/beauty-advisor-sales" element={
        <ProtectedRoute requiredRoles={SALES_DATA_ROLES}>
          <MainLayout>
            <BeautyAdvisorSales />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Read-only extraction, admin-only: the workbook carries advisor-level basket
          figures, and those are staff performance data. brand_manager would be excluded
          anyway -- this module has no supplier scoping, so it would expose other
          suppliers' doors and brands. */}
      <Route path="/advisor-sales-export" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <AdvisorSalesExport />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Roster and supplier assignments, admin-only: the API refuses every other role. */}
      <Route path="/ba-setup" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <BaSetup />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Bangladesh retail calendar and its per-year overrides, admin-only: the API refuses every other role. */}
      <Route path="/master-data/seasonality-calendar" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <SeasonalityCalendar />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Budget Projections - not for brand managers */}
      <Route path="/brand-ranking" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <BrandRanking />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Projections V2 and the legacy Budget Projections: the roles /api/projections-v2
          and /api/budget-projections admit. */}
      <Route path="/projections-v2" element={
        <ProtectedRoute requiredRoles={SALES_DATA_ROLES}>
          <MainLayout>
            <ProjectionsV2 />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/projections-v2/:brandName" element={
        <ProtectedRoute requiredRoles={SALES_DATA_ROLES}>
          <MainLayout>
            <ProjectionV2Page />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/budget-projections" element={
        <ProtectedRoute requiredRoles={SALES_DATA_ROLES}>
          <MainLayout>
            <BudgetProjections />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/budget-projections/:id" element={
        <ProtectedRoute requiredRoles={SALES_DATA_ROLES}>
          <MainLayout>
            <BudgetProjectionPage />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Product Sales List (by Brand): the roles /api/product-sales admits. 'user' is
          not listed, so reporting_manager is named rather than reached through the alias. */}
      <Route path="/budget-projections/product-sales" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'reporting_manager', 'brand_manager']}>
          <MainLayout>
            <ProductSalesPage />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Product Sales Detail (Brand) */}
      <Route path="/budget-projections/product-sales/:brandId" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'reporting_manager', 'brand_manager']}>
          <MainLayout>
            <BrandProductSalesPage />
          </MainLayout>
        </ProtectedRoute>
      } />


      {/* API Keys — credentials for the public product-sales extract API.
          Admin-only, mirroring the backend gate: a key grants read access to sales
          data, so issuing one is treated like handing out a credential. */}
      <Route path="/api-keys" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <ApiKeysPage />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* GA Review — generate / pull / send Generate Analysis data (admin-only, mirrors the backend + sidebar) */}
      {/* Everyone has an inbox, so no role list — a gate here could only keep
          someone from their own notifications. */}
      <Route path="/notifications" element={
        <ProtectedRoute>
          <MainLayout>
            <Notifications />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/ga-data" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <GaDataPage />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* GA PO Selection — selected by Pickup Date / ETA SIN, no NetSuite */}
      <Route path="/ga-new-po" element={
        <ProtectedRoute requiredRoles={['admin', 'manager']}>
          <MainLayout>
            <GaNewPoByDates />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* GA Review — several GAs on one page (?gas=GA-1,GA-2[&month=sep&year=2026]),
          served by the per-GA page. The static segment outranks :gaName below. */}
      <Route path="/ga-data/review" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <GaDetailPage />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* GA Review — per-GA view + edit (quantities) page */}
      <Route path="/ga-data/:gaName" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <GaDetailPage />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Brand YoY Monthly Report, served by /api/product-sales */}
      <Route path="/budget-projections/yoy-report" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'reporting_manager']}>
          <MainLayout>
            <BrandYoyAnalysisPage />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Stock & Inventory: the roles /api/inventory admits (snapshots synced from Product
          Sales, the brand dashboard's Product Sales totals). */}
      <Route path="/inventory" element={
        <ProtectedRoute requiredRoles={SALES_DATA_ROLES}>
          <MainLayout>
            <Inventory />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Purchase & BP Dashboard. brand_manager reaches three of its endpoints for
          Marketing Analytics, not this page. */}
      <Route path="/purchase-bp-dashboard" element={
        <ProtectedRoute requiredRoles={SALES_DATA_ROLES}>
          <MainLayout>
            <PurchaseBPDashboard />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Purchase Orders - not for brand managers */}
      <Route path="/purchase-orders" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <PurchaseOrders />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/purchase-orders/:id" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <PurchaseOrderPage />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/inventory-timeline" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <InventoryTimeline />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/arrival-pipeline" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <ArrivalPipeline />
          </MainLayout>
        </ProtectedRoute>
      } />
      {/* Novelty Pipeline: off unless REACT_APP_NOVELTY_PIPELINE_ENABLED=true; the path then goes
          to the default page. */}
      {noveltyPipelineEnabled() && (
        <Route path="/arrival-pipeline/novelties" element={
          <ProtectedRoute requiredRoles={NOVELTY_PIPELINE_ROLES}>
            <MainLayout>
              <NoveltyPipeline />
            </MainLayout>
          </ProtectedRoute>
        } />
      )}
      {/* Shopify Stock writes to the live storefront's stock: admin only. */}
      <Route path="/shopify-stock" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <ShopifyStock />
          </MainLayout>
        </ProtectedRoute>
      } />
      {/* InvenTree Parts List: admin only while it is tested. */}
      <Route path="/inventree-parts" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <InventreeParts />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Shipment Timeline: off unless REACT_APP_SHIPMENT_TIMELINE_ENABLED=true; the path then
          goes to the default page. */}
      {shipmentTimelineEnabled() && (
        <Route path="/shipment-timeline" element={
          <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
            <MainLayout>
              <ShipmentTimeline />
            </MainLayout>
          </ProtectedRoute>
        } />
      )}

      {/* Brand Targets - not for brand managers */}
      <Route path="/brand-targets" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'user']}>
          <MainLayout>
            <BrandTargetsPage />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Marketing Master Data Routes - Admin/Manager only (not brand managers) */}
      <Route path="/marketing/categories" element={
        <ProtectedRoute requiredRoles={['admin', 'manager']}>
          <MainLayout>
            <MarketingCategories />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/marketing/items" element={
        <ProtectedRoute requiredRoles={['admin', 'manager']}>
          <MainLayout>
            <MarketingItems />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/marketing/contractors" element={
        <ProtectedRoute requiredRoles={['admin', 'manager']}>
          <MainLayout>
            <Contractors />
          </MainLayout>
        </ProtectedRoute>
      } />


      <Route path="/marketing/expenses" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'brand_manager']}>
          <MainLayout>
            <MarketingExpenses />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/capex" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'brand_manager']}>
          <MainLayout>
            <CapexLineItems />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Marketing Quotes - Admin only */}
      <Route path="/marketing/quotes" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <MarketingQuotes />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/marketing/quotes/:id" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <QuoteDetail />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Marketing Invoices - Admin only */}
      <Route path="/marketing/invoices" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <Invoices />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/marketing/invoices/:id" element={
        <ProtectedRoute requiredRoles={['admin']}>
          <MainLayout>
            <InvoiceDetail />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/marketing/reports" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'brand_manager']}>
          <MainLayout>
            <MarketingReports />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/marketing/analytics" element={
        <ProtectedRoute requiredRoles={['admin', 'manager', 'brand_manager']}>
          <MainLayout>
            <MarketingAnalytics />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Marketing > Activation — Dashboard / Activations / Grid View tabs.
          ACTIVATION_ROLES is the one list the sidebar and the backend share. */}
      <Route path="/marketing/activations" element={
        <ProtectedRoute requiredRoles={ACTIVATION_ROLES}>
          <MainLayout>
            <ActivationsHub />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/marketing/activations/:id" element={
        <ProtectedRoute requiredRoles={ACTIVATION_ROLES}>
          <MainLayout>
            <ActivationDetail />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Master Data > Marketing Processes — templates, blocking windows, venues */}
      <Route path="/marketing/processes" element={
        <ProtectedRoute requiredRoles={['admin', 'manager']}>
          <MainLayout>
            <MarketingProcesses />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Catch all - redirect to appropriate home based on role */}
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
    </Suspense>
  );
};

/**
 * Main App Component
 */
function App() {
  // Globally stop wheel-scroll from changing focused number-input values (all
  // 150+ number fields, present and future). See the hook for rationale.
  useDisableNumberInputScroll();

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router>
          <AuthProvider>
            <SidebarProvider>
            <NotificationProvider>
              <AppRoutes />
              <UpdateBanner />
              <ToastContainer
                position="top-center"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
              />
            </NotificationProvider>
            </SidebarProvider>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;

